import { Component } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { SiteService } from "./site.service";
import { ProjectService } from "../project/project.service";
import { ImportITSMSiteDialogComponent } from "./import-itsm-site-dialog.component";
import { RouteHelper, RouteMapping } from "../misc/route-helper";
import { UploaderDialogResult } from "../uploader-wrapper/uploader-dialog.model";
import { DialogService } from "../bootstrap-modal/dialog.service";
import { handleErrors } from "../misc/handle-errors.decorator";
import { AppSettingsService } from "../app-settings/app-settings.service";
import { Query } from "../graphql/query-constant";
import { HasuraGraphqlService } from "../graphql/hasura-graphql-service";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./site-list.template.pug",
  providers: [
    SiteService,
    DialogService,
    ProjectService,
    HasuraGraphqlService,
  ],
})
export class SiteListComponent extends RouteHelper {
  sites: any = [];
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  error: Error;
  projectId: string;
  loading: boolean = true;
  project: any;
  placeholder: string;
  searchSerialNumber: boolean = false;
  isFileDownloading = false;
  contactRefresh = true;
  multiSiteFileStatus: any [] = []
  isS3LinkAvailable: boolean = false;
  mdfUrl = "";
  mesaage: string= "";
  paramsMap: RouteMapping[] = [
    { fieldName: "currentPage", paramName: "page", defaultValue: DEFAULT_PAGE, type: "number" } as RouteMapping,
    { fieldName: "pageSize", paramName: "limit", defaultValue: DEFAULT_PAGE_LIMIT, type: "number" } as RouteMapping,
    { fieldName: "searchText", paramName: "search" } as RouteMapping,
    { fieldName: "projectId", paramName: "projectId" } as RouteMapping,
    { fieldName: "searchSerialNumber", paramName: "searchSerialNumber" } as RouteMapping,
  ];

  constructor(
    private siteService: SiteService,
    private projectService: ProjectService,
    public route: ActivatedRoute,
    public router: Router,
    private appSettingsService: AppSettingsService,
    public dialogService: DialogService,
    public hasuraGqlService: HasuraGraphqlService,
  ) {
    super();
  }

  navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["sites"], { queryParams, skipLocationChange });
  }

  setFileDownload(evt) {
    this.isFileDownloading = evt;
  }

  @handleErrors()
  async loadData() {
    this.loading = true;
    if (!this.projectId) {
      throw new Error("Please go back to the main page and try again.");
    }
    Query.MULTI_SITE_FILE_STAUS.variables.projectId = this.projectId;
    const params = {
      projectId: this.projectId,
      query: Query.MULTI_SITE_FILE_STAUS,
      uiAction: "SITE-DETAILS",
    }
    const multiSiteFileStatus = await this.hasuraGqlService.getResponseFromHasura(params);
    if (multiSiteFileStatus && multiSiteFileStatus.body && multiSiteFileStatus.body.data &&
      multiSiteFileStatus.body.data.multisite_export_status && multiSiteFileStatus.body.data.multisite_export_status.length > 0) {
        if(multiSiteFileStatus.body.data.multisite_export_status[0].file_url && multiSiteFileStatus.body.data.multisite_export_status[0].file_status === 'valid') {
          this.isS3LinkAvailable = true;
          this.mdfUrl = multiSiteFileStatus.body.data.multisite_export_status[0].file_url;
          const urlExpiretime = this.mdfUrl.split("Expires=")[1].split("&Signature=")[0]
          this.mesaage = "to download the file.";
          if(new Date().getTime().toString() > urlExpiretime.toString()) {
            this.isS3LinkAvailable = false;
            this.mesaage = "";
          }
        } else if(multiSiteFileStatus.body.data.multisite_export_status[0].file_status === 'in-progress') {
          this.mesaage = "Download will be available shortly in Multisite Design drop down section. Please wait.";
        } else {
          this.mesaage = ""
        }
    }
    const { rows, count } = await this.siteService.getSites(
      +(this.currentPage) - 1,
      +(this.pageSize),
      this.searchText,
      this.projectId,
      this.searchSerialNumber,
    );

    this.sites = rows;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;

    this.loading = false;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.syncRoute();
  }

  uploadModalClosed(event: any): void {
    const result = event["value"] as UploaderDialogResult;

    if (result.responses.length) {
      this.loadData();
    }
  }

  changePlaceHolder() {
    if (this.searchSerialNumber) this.placeholder = "Enter Complete Serial Number";
    else this.placeholder = null;
    this.syncRoute();
  }

  async addITSMSite(): Promise<any> {
    const result = await ImportITSMSiteDialogComponent.showImportITSMSitesDialogModal(
      this.dialogService,
      { projectId: this.projectId },
    );

    if (!result) return;

    await this.loadData();
  }

  public async exportMultiSiteMdf() {  
    this.isFileDownloading = true;
    this.isS3LinkAvailable = false;
    this.mesaage = 'Download will be available shortly in Multisite Design drop down section. Please wait.';
    Query.MULTI_SITE_FILE_STAUS.variables.projectId = this.projectId;
    const params = {
      projectId: this.projectId,
      query: Query.MULTI_SITE_FILE_STAUS,
      uiAction: "SITE-DETAILS",
    }
    const multiSiteFileStatus = await this.hasuraGqlService.getResponseFromHasura(params);
    if (multiSiteFileStatus && multiSiteFileStatus.body && multiSiteFileStatus.body.data &&
      multiSiteFileStatus.body.data.multisite_export_status && multiSiteFileStatus.body.data.multisite_export_status.length > 0) {
        if(multiSiteFileStatus.body.data.multisite_export_status[0].file_status === 'in-progress') {
          this.isFileDownloading = false;
          return;
        }
    }
    let mdfExpRes = await this.siteService.exportPartnerInstallationFile(
      this.projectId
    );

    const handleMdfResponse = async (it) =>{
      if (it.status === 2) {
        this.mesaage = "to download the file.";
        this.isS3LinkAvailable = true;
        this.mdfUrl = it.url;
      } else if (it.status === 3) {
        this.mesaage = "MDF export failed. Please try again.";
        this.isS3LinkAvailable = false;
        this.multiSiteFileStatus.forEach((sub) => {
          sub.subscription.unsubscribe();
        });
      }  else {
        this.isS3LinkAvailable = false;
       // keep listening
      }
    }
    if (mdfExpRes.status === 'Success') {
      //this.mesaage = mdfExpRes.content.message;
      this.isFileDownloading = false;
      const statusObservable = this.siteService.listenToTransactionStatus(this.projectId)
      this.multiSiteFileStatus.push({
        subscription: statusObservable.subscribe(handleMdfResponse),
        error: false,
      })
    }
  }

  public async downloadMultiSiteMdf() {
    if (this.mdfUrl) {
      //window.open(this.mdfUrl, "_blank");
      window.location.href = this.mdfUrl
    }
  }
}
